import { StarOff } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Section } from "../admin/ui.tsx";
import { useFavourites, useSpots, useToggleFavourite } from "../api/index.ts";
import { Button } from "../components/Button.tsx";
import { useLocale } from "../i18n/index.ts";

/**
 * "Meine Favoriten" for every signed-in role: the favourite spots (newest first) with name and water
 * body from the public spot list, a link to each and a remove button. Private: only the account sees it.
 */
export function FavouritesSection() {
  const { t } = useTranslation();
  const locale = useLocale();
  const favourites = useFavourites();
  const spots = useSpots();
  const toggle = useToggleFavourite();

  // `ids` keeps the server's order (newest first); a spot the public list does not know is skipped
  const rows = useMemo(() => {
    const byId = new Map((spots.data ?? []).map((s) => [s.id, s]));
    return [...favourites.ids].flatMap((id) => {
      const s = byId.get(id);
      return s ? [s] : [];
    });
  }, [favourites.ids, spots.data]);

  const loading = favourites.isPending || (favourites.ready && spots.isPending);
  const failed = favourites.isError || (favourites.ready && spots.isError);

  return (
    <Section title={t("favourites.title")}>
      <p className="ds-text-subheadline text-label-secondary m-0">{t("favourites.intro")}</p>
      {failed ? (
        <div className="grid gap-2 justify-items-start" role="alert">
          <p className="ds-text-subheadline m-0">{t("favourites.loadError")}</p>
          <Button
            variant="tinted"
            onClick={() => {
              favourites.refetch();
              void spots.refetch();
            }}
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : loading ? (
        <p className="ds-text-subheadline text-label-secondary m-0" role="status">
          {t("common.loading")}
        </p>
      ) : rows.length === 0 ? (
        <p className="ds-text-subheadline m-0">{t("favourites.empty")}</p>
      ) : (
        <ul className="ds-list adm-list" aria-label={t("favourites.title")}>
          {rows.map((s) => {
            const place = [s.locality, s.waterBody].filter(Boolean).join(" · ");
            return (
              <li key={s.id} className="ds-row">
                <Link
                  className="ds-row__title min-w-0"
                  to={`/${locale}/spot/${s.slug}`}
                  data-favourite-spot={s.slug}
                >
                  <span className="ds-text-headline">{s.name}</span>
                  {place ? (
                    <span className="ds-text-subheadline text-label-secondary block">{place}</span>
                  ) : null}
                </Link>
                <Button
                  variant="plain"
                  aria-label={t("favourites.removeNamed", { name: s.name })}
                  icon={<StarOff aria-hidden="true" strokeWidth={1.75} />}
                  onClick={() => void toggle(s.id, false)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
